/*
Fork addition: derived flow diagnostics shared by the top balance
bar, the factory-card STARVED / BLOCKED badge and the inspector
warning lines. Everything here is computed from the SOLVED model
state the UI already renders (io.cpsSolvedTotal / io.cpsMaxTotal,
link.flow, item.solvedCount / item.count) — no solver changes, no
save-format changes.
*/
import {GameItemType} from '#types/constants';
import type {GameItem} from '#types/game-data';
import type {
    BlueprintItemModel,
    BlueprintModel,
    RecipeIOModel,
} from './model/store';

//Pseudo-products that are not stock goods (workers, electricity,
//maintenance, computing, pollution, ...) — the same Special / Energy
//set that storage.ts refuses to match through abstract classes and
//transport.ts reports as having no in-game transport.
export function isStockProduct(item: GameItem | undefined): boolean {
    if(!item)
        return false;
    return (item.type !== GameItemType.Special)
        && (item.type !== GameItemType.Energy);
}

/* ------------------------------------------------------------------ */
/* Net balance of the whole board (top balance bar)                    */
/* ------------------------------------------------------------------ */

export interface NetBalanceEntry {
    item: GameItem;
    //positive rate (per minute, the game's solved-rate convention):
    //for needsIn it must come from outside the plan, for surplusOut
    //it is produced but not consumed inside the plan
    rate: number;
}

export interface NetBalance {
    needsIn: NetBalanceEntry[];
    surplusOut: NetBalanceEntry[];
    //false when nothing on the board has been solved yet
    hasSolved: boolean;
}

const BALANCE_EPSILON = 1e-6;

//per product across ALL items at solved rates:
//net = total produced - total consumed. Linked flows cancel out;
//what remains is exactly what the plan pulls from outside (net < 0)
//and what it hands back unconsumed (net > 0).
export function calculateNetBalance(model: BlueprintModel): NetBalance {
    const gameData = model.gameData;
    const netByName = new Map<string, {item: GameItem; net: number}>();
    let hasSolved = false;
    for(const blueprintItem of model.items) {
        for(const io of blueprintItem.selectedRecipe?.items || []) {
            if(io.isAbstractClassItem && !io.isMatherialized) {
                //only real products can be attributed
                continue;
            }
            const cpsSolved = io.cpsSolvedTotal;
            if(!cpsSolved || !io.name)
                continue;
            const item = gameData.getGameItem(io.name);
            if(!isStockProduct(item))
                continue;
            hasSolved = true;
            let entry = netByName.get(io.name);
            if(!entry) {
                entry = {item: item!, net: 0};
                netByName.set(io.name, entry);
            }
            entry.net += io.isInput ? -cpsSolved : cpsSolved;
        }
    }
    const needsIn: NetBalanceEntry[] = [];
    const surplusOut: NetBalanceEntry[] = [];
    for(const {item, net} of netByName.values()) {
        if(net < -BALANCE_EPSILON)
            needsIn.push({item, rate: -net});
        else if(net > BALANCE_EPSILON)
            surplusOut.push({item, rate: net});
    }
    //same ordering the summary panel uses
    const byOrder = (a: NetBalanceEntry, b: NetBalanceEntry) => a.item.order - b.item.order;
    needsIn.sort(byOrder);
    surplusOut.sort(byOrder);
    return {needsIn, surplusOut, hasSolved};
}

/* ------------------------------------------------------------------ */
/* STARVED / BLOCKED per factory                                       */
/* ------------------------------------------------------------------ */

export type FlowStatusKind = 'starved' | 'blocked';

export interface ItemFlowStatus {
    kind: FlowStatusKind;
    //the io whose linked side explains the throttle
    io: RecipeIOModel;
    //the io's rate at the item's target count (cpsMaxTotal)
    targetRate: number;
    //sum of solved flow over the io's links
    linkedRate: number;
    //linkedRate / targetRate
    coverage: number;
}

//an item counts as throttled below this fraction of its target, and a
//linked side "explains" the throttle when its coverage is below the
//same bound and matches the solved fraction within this tolerance
const THROTTLE_FRACTION = 0.995;
const COVERAGE_TOLERANCE = 0.02;

function linkedRate(io: RecipeIOModel): number {
    let sum = 0;
    for(const link of io.links)
        sum += link.flow ?? 0;
    return sum;
}

//STARVED: an objective item runs below its target because a linked
//input cannot be supplied faster (the receivers pause in-game).
//BLOCKED: an objective item runs below its target because a linked
//output cannot be taken away faster (the producer pauses in-game).
//
//The objective gate is deliberate, not a shortcut: a non-objective
//item has no rate of its own — the solver sets it to whatever the
//chain demands, so for every pulled factory in a clean chain the
//linked coverage equals the solved fraction trivially, and badging
//them would flag every healthy chain (e.g. the concrete chain's
//kiln/crusher, pulled to 50% / 37.5% by their objective mixer).
//Only an objective item's count is a target the solver can miss.
//Unlinked inputs/outputs never trigger a badge: external supply and
//disposal are the balance bar's story, not a fault.
export function itemFlowStatus(item: BlueprintItemModel): ItemFlowStatus | undefined {
    if(item.objective === undefined)
        return undefined;
    const solvedCount = item.solvedCount;
    const targetCount = item.count;
    if((solvedCount === undefined) || !(targetCount > 0))
        return undefined;
    const solvedFraction = solvedCount / targetCount;
    if(solvedFraction >= THROTTLE_FRACTION)
        return undefined;
    const recipe = item.selectedRecipe;
    if(!recipe)
        return undefined;

    let worstInput: ItemFlowStatus | undefined;
    let worstOutput: ItemFlowStatus | undefined;
    for(const io of recipe.items) {
        if(!io.linksCount)
            continue;
        const targetRate = io.cpsMaxTotal;
        if(!(targetRate > 0))
            continue;
        const linked = linkedRate(io);
        const coverage = linked / targetRate;
        const candidate: ItemFlowStatus = {
            kind: io.isInput ? 'starved' : 'blocked',
            io,
            targetRate,
            linkedRate: linked,
            coverage,
        };
        if(io.isInput) {
            if(!worstInput || (coverage < worstInput.coverage))
                worstInput = candidate;
        } else {
            if(!worstOutput || (coverage < worstOutput.coverage))
                worstOutput = candidate;
        }
    }
    const explains = (candidate?: ItemFlowStatus) => !!candidate
        && (candidate.coverage < THROTTLE_FRACTION)
        && (Math.abs(candidate.coverage - solvedFraction) <= COVERAGE_TOLERANCE);
    if(explains(worstInput))
        return worstInput;
    if(explains(worstOutput))
        return worstOutput;
    return undefined;
}
